import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  async showQuickLook(event) {
    event.preventDefault();

    const response = await this.performFetch(event.currentTarget.href, "GET");

    if (response.ok) {
      const quickViewElement = document.querySelector("#driving-school-evaluations-quick-look");
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");

      if (template) {
        quickViewElement.innerHTML = "";
        quickViewElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async validate(event) {
    event.preventDefault();
    const successMessage = event.currentTarget.dataset.successMessage || "Changes saved successfully.";
    const saveButton = event.currentTarget;

    this.disableButton(saveButton, "Saving...");
    this.hideErrorPanel();

    const errors = this.validateForm();

    if (errors.length > 0) {
      this.enableButton(saveButton);
      this.showErrorPanel(errors.join("<br>"));
      return;
    }

    const form = event.target.closest("form");
    const formData = new FormData(form);
    const response = await this.performFetch(form.action, form.method, formData);

    if (response.ok) {
      const stream = await response.text();
      Turbo.renderStreamMessage(stream);
      this.hideQuickLook();
      this.showToast(successMessage);
    } else if (response.error) {
      this.showErrorPanel(response.error);
    } else {
      try {
        const responseBody = await response.json();
        if (responseBody.errors && responseBody.errors.length) {
          this.showErrorPanel(responseBody.errors.join("<br>"));
        } else if (responseBody.error) {
          this.showErrorPanel(responseBody.error);
        } else {
          this.showErrorPanel("Something has gone wrong. Please try again later.");
        }
      } catch (error) {
        this.showErrorPanel("Something has gone wrong. Please try again later.");
      }
    }

    this.enableButton(saveButton);
  }

  validateForm() {
    const validationErrors = [];
    const form = document.querySelector("#driving_school_evaluations_quick_look form");
    const nameField = form ? form.querySelector('input[name$="[name]"]') : null;
    const nameLabel = nameField?.dataset.validationName || "Name";

    if (nameField && nameField.value.trim() === "") {
      validationErrors.push(`${nameLabel} cannot be empty.`);
    }

    return validationErrors;
  }

  async confirmRemove(event) {
    event.preventDefault();
    const successMessage = event.currentTarget.dataset.successMessage || "Changes saved successfully.";
    const removeButton = event.currentTarget;

    this.disableButton(removeButton, "Removing...");
    this.hideErrorPanel();

    const response = await this.performFetch(event.currentTarget.dataset.url, "PATCH");

    if (response.ok) {
      const stream = await response.text();
      Turbo.renderStreamMessage(stream);
      this.hideQuickLook();
      this.showToast(successMessage);
    } else if (response.error) {
      this.showErrorPanel(response.error);
    } else {
      this.showErrorPanel("Failed to remove the template. Please try again later.");
    }

    this.enableButton(removeButton);
  }

  showRemovePanel(event) {
    event.preventDefault();

    const removePanel = document.getElementById("remove-confirmation-panel");
    removePanel.removeAttribute("hidden");
  }

  hideRemovePanel(event) {
    event.preventDefault();

    const removePanel = document.getElementById("remove-confirmation-panel");
    removePanel.setAttribute("hidden", true);
  }

  closeQuickLook(event) {
    event.preventDefault();

    this.hideQuickLook();
  }

  hideQuickLook() {
    const quickViewElement = document.querySelector("#driving-school-evaluations-quick-look");
    quickViewElement.classList.add("u-hidden");
  }

  showToast(message) {
    window.dispatchEvent(new CustomEvent("toast:show", {
      detail: { message: message }
    }));
  }

  showErrorPanel(message) {
    const errorPanel = document.getElementById("error-panel");
    const errorMessage = document.getElementById("error-message");

    errorMessage.innerHTML = message;
    errorPanel.hidden = false;
    errorMessage.hidden = false;
  }

  hideErrorPanel() {
    const errorPanel = document.getElementById("error-panel");
    const errorMessage = document.getElementById("error-message");

    if (!errorPanel || !errorMessage) return;

    errorMessage.innerHTML = "";
    errorPanel.hidden = true;
    errorMessage.hidden = true;
  }

  disableButton(button, loadingText) {
    if (!button) return;

    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent.trim();
    }

    button.disabled = true;
    button.classList.add("Button--disabled");
    button.textContent = loadingText;
  }

  enableButton(button) {
    if (!button) return;

    button.disabled = false;
    button.classList.remove("Button--disabled");

    if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
    }
  }

  async performFetch(url, method, body = null) {
    const csrfToken = document.querySelector("meta[name='csrf-token']").content;
    const headers = {
      "X-CSRF-Token": csrfToken,
      "Accept": "text/vnd.turbo-stream.html"
    };

    const options = {
      method: method,
      headers: headers
    };

    if (body) {
      options.body = body;
    }

    try {
      return await fetch(url, options);
    } catch (error) {
      console.error("Error performing fetch: ", error);
      return { error: "Network error, please check your connection and try again." };
    }
  }
}
